import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Grid,
  Paper,
  Stack,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";

import {
  TrendingUp,
  Timeline,
  AttachMoney,
  Warning,
  CheckCircle,
  Water,
  BarChart,
} from "@mui/icons-material";
import { useGetFinancialAnalysisQuery } from "../../../redux/Slices/financial-analysisApi";
import detectFiscalPeriod from "hooks/detectFiscalPeriod";
import { useGetFinancialPeriodsQuery } from "../../../redux/Slices/financial-periodsApi";
import MDBox from "components/MDBox";

// Translations
const translations = {
  ar: {
    title: "تقرير التحليل المالي",
    liquidityRatios: "نسب السيولة",
    profitabilityRatios: "نسب الربحية",
    solvencyRatios: "نسب المديونية",
    currentRatio: "النسبة الجارية",
    quickRatio: "نسبة السيولة السريعة",
    netProfitMargin: "هامش صافي الربح",
    returnOnAssets: "العائد على الأصول",
    returnOnEquity: "العائد على حقوق الملكية",
    debtToEquity: "نسبة الدين إلى حقوق الملكية",
    debtToAssets: "نسبة الدين إلى الأصول",
    equityRatio: "نسبة حقوق الملكية",
    fiscalPeriod: "الفترة المالية",
    analysisType: "نوع التحليل",
    generatedAt: "تم الإنشاء في",
    loading: "جاري التحميل...",
    error: "حدث خطأ أثناء تحميل البيانات",
    comprehensive: "شامل",
    excellent: "ممتاز",
    good: "جيد",
    fair: "مقبول",
    warning: "تحذير",
    poor: "ضعيف",
    switchLang: "English",
    currentRatioDesc: "يقيس قدرة الشركة على سداد الالتزامات قصيرة الأجل",
    quickRatioDesc: "يقيس السيولة الفورية دون احتساب المخزون",
    netProfitMarginDesc: "نسبة الربح الصافي من إجمالي الإيرادات",
    roaDesc: "كفاءة استخدام الأصول لتحقيق الأرباح",
    roeDesc: "عائد المستثمرين على حقوق الملكية",
    debtToEquityDesc: "نسبة الدين مقارنة بحقوق الملكية",
    debtToAssetsDesc: "نسبة الديون من إجمالي الأصول",
    equityRatioDesc: "نسبة حقوق الملكية من إجمالي الأصول",
  },
  en: {
    title: "Financial Analysis Report",
    liquidityRatios: "Liquidity Ratios",
    profitabilityRatios: "Profitability Ratios",
    solvencyRatios: "Solvency Ratios",
    currentRatio: "Current Ratio",
    quickRatio: "Quick Ratio",
    netProfitMargin: "Net Profit Margin",
    returnOnAssets: "Return on Assets",
    returnOnEquity: "Return on Equity",
    debtToEquity: "Debt to Equity",
    debtToAssets: "Debt to Assets",
    equityRatio: "Equity Ratio",
    fiscalPeriod: "Fiscal Period",
    analysisType: "Analysis Type",
    generatedAt: "Generated At",
    loading: "Loading...",
    error: "Error loading data",
    comprehensive: "Comprehensive",
    excellent: "Excellent",
    good: "Good",
    fair: "Fair",
    warning: "Warning",
    poor: "Poor",
    switchLang: "العربية",
    currentRatioDesc: "Measures ability to pay short-term obligations",
    quickRatioDesc: "Measures immediate liquidity without inventory",
    netProfitMarginDesc: "Net profit percentage of total revenue",
    roaDesc: "Efficiency of using assets to generate profit",
    roeDesc: "Return on shareholders equity investment",
    debtToEquityDesc: "Debt ratio compared to equity",
    debtToAssetsDesc: "Debt percentage of total assets",
    equityRatioDesc: "Equity percentage of total assets",
  },
};

// Health status calculator
const getHealthStatus = (value, type, metric) => {
  const statuses = translations.en;

  if (type === "liquidity") {
    if (metric === "current") {
      if (value >= 2) return { status: statuses.excellent, color: "success", level: 100 };
      if (value >= 1.5) return { status: statuses.good, color: "success", level: 75 };
      if (value >= 1) return { status: statuses.fair, color: "warning", level: 50 };
      return { status: statuses.poor, color: "error", level: 25 };
    }
    if (metric === "quick") {
      if (value >= 1.5) return { status: statuses.excellent, color: "success", level: 100 };
      if (value >= 1) return { status: statuses.good, color: "success", level: 75 };
      if (value >= 0.75) return { status: statuses.fair, color: "warning", level: 50 };
      return { status: statuses.poor, color: "error", level: 25 };
    }
  }

  if (type === "profitability") {
    if (metric === "margin") {
      if (value >= 15) return { status: statuses.excellent, color: "success", level: 100 };
      if (value >= 10) return { status: statuses.good, color: "success", level: 75 };
      if (value >= 5) return { status: statuses.fair, color: "warning", level: 50 };
      return { status: statuses.poor, color: "error", level: 25 };
    }
    if (metric === "roa") {
      if (value >= 10) return { status: statuses.excellent, color: "success", level: 100 };
      if (value >= 7) return { status: statuses.good, color: "success", level: 75 };
      if (value >= 4) return { status: statuses.fair, color: "warning", level: 50 };
      return { status: statuses.poor, color: "error", level: 25 };
    }
    if (metric === "roe") {
      if (value >= 20) return { status: statuses.excellent, color: "success", level: 100 };
      if (value >= 15) return { status: statuses.good, color: "success", level: 75 };
      if (value >= 10) return { status: statuses.fair, color: "warning", level: 50 };
      return { status: statuses.poor, color: "error", level: 25 };
    }
  }

  if (type === "solvency") {
    if (metric === "debtToEquity") {
      if (value <= 0.5) return { status: statuses.excellent, color: "success", level: 100 };
      if (value <= 1) return { status: statuses.good, color: "success", level: 75 };
      if (value <= 1.5) return { status: statuses.fair, color: "warning", level: 50 };
      return { status: statuses.warning, color: "error", level: 25 };
    }
    if (metric === "debtToAssets") {
      if (value <= 0.3) return { status: statuses.excellent, color: "success", level: 100 };
      if (value <= 0.5) return { status: statuses.good, color: "success", level: 75 };
      if (value <= 0.7) return { status: statuses.fair, color: "warning", level: 50 };
      return { status: statuses.warning, color: "error", level: 25 };
    }
    if (metric === "equity") {
      if (value >= 0.7) return { status: statuses.excellent, color: "success", level: 100 };
      if (value >= 0.5) return { status: statuses.good, color: "success", level: 75 };
      if (value >= 0.3) return { status: statuses.fair, color: "warning", level: 50 };
      return { status: statuses.poor, color: "error", level: 25 };
    }
  }

  return { status: statuses.good, color: "default", level: 50 };
};

// Ratio Card Component
const RatioCard = ({ title, value, description, icon: Icon, type, metric, isRTL }) => {
  const health = getHealthStatus(value, type, metric);

  return (
    <Card
      sx={{
        height: "100%",
        borderLeft: 4,
        borderColor: "grey.200",
        transition: "all 0.3s ease",
        "&:hover": {
          boxShadow: 6,
          borderColor: "primary.main",
        },
      }}
    >
      <CardContent>
        <Box
          sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 2 }}
        >
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Icon color={health.color} />
              <Typography variant="h6" component="h3" sx={{ fontWeight: 600 }}>
                {title}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.main">
              {description}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", mb: 2 }}
        >
          <Box>
            <Typography variant="h4" component="div" sx={{ fontWeight: "bold" }}>
              {value.toFixed(2)}
              {type === "profitability" ? "%" : ""}
            </Typography>
          </Box>
          <Chip
            label={
              isRTL ? translations.ar[health.status.toLowerCase()] || health.status : health.status
            }
            color={health.color}
            variant="outlined"
            size="small"
          />
        </Box>

        <Box sx={{ position: "relative" }}>
          <LinearProgress
            variant="determinate"
            value={health.level}
            color={health.color}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: "grey.200",
              "& .MuiLinearProgress-bar": {
                borderRadius: 4,
              },
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

// Section Header Component
const SectionHeader = ({ title, icon: Icon, color }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
    <Paper
      sx={{
        p: 1.5,
        borderRadius: 2,
        backgroundColor: color,
      }}
    >
      <Icon sx={{ fontSize: 28, color: "white" }} />
    </Paper>
    <Typography variant="h4" component="h2" sx={{ fontWeight: "bold" }}>
      {title}
    </Typography>
  </Box>
);

// create FinancialAnalysisReport
const FinancialAnalysisReport = () => {
  const [periodId, setPeriodId] = useState(() => {
    return detectFiscalPeriod(new Date().toISOString().split("T")[0]);
  });
  const { data: periods } = useGetFinancialPeriodsQuery();
  const [isRTL, setIsRTL] = useState(true);

  const filterQuery = new URLSearchParams({
    fiscal_period_id: periodId?.toString(),
  });

  const { data, error, isLoading } = useGetFinancialAnalysisQuery(filterQuery.toString());
  const t = translations[isRTL ? "ar" : "en"];
  

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          background: `linear-gradient(135deg, "#09c6f9"}, "#045de9")`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          direction: isRTL ? "rtl" : "ltr",
        }}
      >
        <Box sx={{ textAlign: "center" }}>
          <CircularProgress size={60} thickness={4} sx={{ mb: 2 }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t.loading}
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          background: `linear-gradient(135deg, " #09c6f9", "#045de9")`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
          direction: isRTL ? "rtl" : "ltr",
        }}
      >
        <Paper
          sx={{
            p: 3,
            borderLeft: 4,
            borderColor: "error.main",
            backgroundColor: "error.light",
            maxWidth: 400,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Warning color="error" />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: "error.dark" }}>
                {t.error}
              </Typography>
              <Typography variant="body2" color="error.main" sx={{ mt: 0.5 }}>
                {error.status || "Failed to fetch data"}
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <MDBox
       p={3}
    >
      <Card >
        {/* Header */}
        <MDBox
           mx={2}
            mt={-3}
            py={3}
            px={2}
            borderRadius="lg"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              background: "linear-gradient(90deg, #ca9937, #e0b15a)",
              boxShadow: 3,
            }}
          flexWrap="wrap"
          gap={2}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Paper
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  backgroundColor: "primary.main",
                }}
              >
                <BarChart sx={{ fontSize: 32, color: "white" }} />
              </Paper>
              <Box>
                <Typography variant="h4" component="h1" sx={{ fontWeight: "bold" }}>
                  {t.title}
                </Typography>
                {data?.meta && (
                  <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
                    {t.fiscalPeriod}:{" "}
                    <Typography component="span" sx={{ fontWeight: 600 }}>
                      {data.meta.fiscal_period}
                    </Typography>
                  </Typography>
                )}
              </Box>
            </Box>
            <Box>
              <FormControl flex={1} sx={{ mb: 2 }}>
                <InputLabel id="type"> اختر الفترة </InputLabel>
                <Select
                  label="اختر الفترة "
                  /* error={!!errors.type} */
                  name="type"
                  value={periodId}
                  onChange={(e) => {
                    setPeriodId(e.target.value);
                  }}
                  sx={{ height: 43 }}
                >
                  {periods?.data?.map((period) => (
                    <MenuItem key={period.id} value={period.id}>
                      {period.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>

          {data?.meta && (
            <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: "wrap", gap: 1 }}>
              <Chip
                label={`${t.analysisType}: ${t.comprehensive}`}
                color="primary"
                variant="outlined"
                size="small"
              />
              <Chip
                label={`${t.generatedAt}: ${data.meta.generated_at}`}
                color="default"
                variant="outlined"
                size="small"
              />
            </Stack>
          )}
        </MDBox>
        
        <Box m={2}>

       
        {/* Liquidity Ratios */}
        <Box sx={{ mb: 6 }}>
          <SectionHeader title={t.liquidityRatios} icon={Water} color="primary.main" />
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <RatioCard
                title={t.currentRatio}
                value={data?.data?.liquidity_ratios.current_ratio}
                description={t.currentRatioDesc}
                icon={Timeline}
                type="liquidity"
                metric="current"
                isRTL={isRTL}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <RatioCard
                title={t.quickRatio}
                value={data?.data?.liquidity_ratios.quick_ratio}
                description={t.quickRatioDesc}
                icon={TrendingUp}
                type="liquidity"
                metric="quick"
                isRTL={isRTL}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Profitability Ratios */}
        <Box sx={{ mb: 6 }}>
          <SectionHeader title={t.profitabilityRatios} icon={TrendingUp} color="success.main" />
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <RatioCard
                title={t.netProfitMargin}
                value={data?.data?.profitability_ratios.net_profit_margin}
                description={t.netProfitMarginDesc}
                icon={AttachMoney}
                type="profitability"
                metric="margin"
                isRTL={isRTL}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <RatioCard
                title={t.returnOnAssets}
                value={data?.data?.profitability_ratios.return_on_assets}
                description={t.roaDesc}
                icon={Timeline}
                type="profitability"
                metric="roa"
                isRTL={isRTL}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <RatioCard
                title={t.returnOnEquity}
                value={data?.data?.profitability_ratios.return_on_equity}
                description={t.roeDesc}
                icon={TrendingUp}
                type="profitability"
                metric="roe"
                isRTL={isRTL}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Solvency Ratios */}
        <Box>
          <SectionHeader title={t.solvencyRatios} icon={BarChart} color="warning.main" />
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <RatioCard
                title={t.debtToEquity}
                value={data?.data?.solvency_ratios.debt_to_equity}
                description={t.debtToEquityDesc}
                icon={Timeline}
                type="solvency"
                metric="debtToEquity"
                isRTL={isRTL}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <RatioCard
                title={t.debtToAssets}
                value={data?.data?.solvency_ratios.debt_to_assets}
                description={t.debtToAssetsDesc}
                icon={BarChart}
                type="solvency"
                metric="debtToAssets"
                isRTL={isRTL}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <RatioCard
                title={t.equityRatio}
                value={data?.data?.solvency_ratios.equity_ratio}
                description={t.equityRatioDesc}
                icon={CheckCircle}
                type="solvency"
                metric="equity"
                isRTL={isRTL}
              />
            </Grid>
          </Grid>
        </Box>
         </Box>
      </Card>
    </MDBox>
  );
};

export default FinancialAnalysisReport;
